import { EntityAdapter, EntityState } from "@reduxjs/toolkit";
import { store } from "@store/store";
// eslint-disable-next-line no-restricted-imports -- The only place needed to define useAppDispatch and useAppSelector
import { useDispatch, useSelector } from "react-redux";

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
type AppDispatch = typeof store.dispatch;

// Use throughout app instead of plain `useDispatch` and `useSelector` to get type-safety
// Link: https://react-redux.js.org/using-react-redux/usage-with-typescript#withtypes
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();

/**
 * Our IDs like projectId and companyId are only string. However, EntityId from redux-toolkit is in type of string | number.
 * Therefore, we defined our own narrower alias that only supports string
 */
type StringEntityId = string;

/**
 * Defines the base state for all Redux stores that contain one entity adapter,
 * which serves to store a map with the main entity type for that particular store.
 *
 * @see https://redux-toolkit.js.org/api/createEntityAdapter
 */
export type BaseEntityState<T> = EntityState<T, StringEntityId>;

/**
 * Defines the base adapter type for managing a collection of entities in Redux stores.
 * Provides prebuilt reducer functions and selectors for the given entity type `T`,
 * while ensuring that entity IDs are represented as strings.
 *
 * @see https://redux-toolkit.js.org/api/createEntityAdapter
 */
export type BaseEntityAdapter<T> = EntityAdapter<T, StringEntityId>;

/** Status of the action to fetch data from the backend */
export type FetchingStatus =
  "uninitialized" | "pending" | "succeeded" | "rejected";
